import type { BlogPost } from "../../lib/blog";

export const secretSantaEntreCollegues: BlogPost = {
  h1: "Organiser un Secret Santa entre collègues : budget, règles et tirage",
  seoTitle: "Secret Santa entre collègues : organiser un Père Noël secret au bureau · Listou",
  seoDescription:
    "Le Secret Santa au bureau, de l'annonce au jour J : le budget qui ne gêne personne, la participation libre, le tirage en ligne sans compte, les exclusions par équipe et les cadeaux à éviter.",
  publishedOn: "2026-10-20",
  updatedOn: "2026-10-20",
  summary:
    "Un Secret Santa entre collègues, ou Père Noël secret au bureau, réussit quand le budget est petit (10 à 15 €), la participation libre, et le tirage fait en ligne pour que personne ne tombe sur soi-même ni sur son manager. Annoncez-le trois semaines avant le dernier jour de bureau, demandez à chacun deux ou trois envies, et fixez l'échange à la pause déjeuner.",
  body: `
## Pourquoi un Secret Santa au bureau

Le Père Noël secret est né entre collègues, et c'est encore là qu'il fonctionne le mieux. Il donne une raison de parler à des gens qu'on croise sans les connaître, il coûte le prix d'un déjeuner, et il ne demande à personne de choisir qui mérite un cadeau. Chacun en offre un et en reçoit un, point.

Ce qui le fait rater est toujours la même chose : un budget que certains trouvent trop élevé, une participation ressentie comme obligatoire, ou un cadeau déplacé. Les règles ci-dessous servent à éviter ces trois écueils.

## Le budget : petit, et annoncé comme un maximum

Au bureau, **10 à 15 €** est le bon montant. C'est assez pour un cadeau qui fait plaisir, et assez peu pour que l'alternant, le stagiaire ou le collègue à mi-temps participent sans y réfléchir. Au-delà de 20 €, certains s'abstiendront, et ils ne diront pas pourquoi.

Annoncez le budget comme un plafond : « 15 € maximum ». Un montant à viser pousse à dépasser ; un plafond protège ceux qui s'en tiennent au minimum. Et précisez qu'un cadeau fait main ou à prix cassé est le bienvenu.

## La participation est libre, et on le dit

Personne ne doit se sentir inscrit d'office. Envoyez l'annonce à toute l'équipe avec une date limite pour répondre, et ne tirez au sort que ceux qui ont dit oui. Un collègue qui n'aime pas Noël, qui traverse une période difficile ou qui n'a pas envie de dépenser a le droit de ne pas participer sans se justifier.

Trois personnes suffisent pour un tirage. Si l'équipe est grande, entre vingt et cinquante, le jeu marche toujours, mais la devinette du jour J devient impossible : faites un tirage par service ou par plateau.

## Le calendrier qui marche

- **Trois semaines avant** le dernier jour de bureau : l'annonce, avec le budget, la date de l'échange et la date limite pour s'inscrire.
- **Deux semaines avant** : le tirage. Chacun reçoit son lien et découvre la personne tirée et ses envies.
- **Une semaine avant** : un rappel à ceux qui n'ont pas encore ouvert leur tirage. Listou l'envoie tout seul, une semaine puis deux jours avant l'échange.
- **Le jour J** : l'échange, à la pause déjeuner ou au pot de fin d'année, pour que les collègues en télétravail puissent venir.

## Le tirage en ligne, sans compte

Le bonnet et les petits papiers demandent tout le monde au même endroit, le même jour. Avec le télétravail, ce jour n'existe plus. Un tirage en ligne envoie à chacun son résultat par email, où qu'il soit, et exclut d'office qu'on tire son propre nom.

Sur [Listou](/secret-santa), l'organisateur ajoute les prénoms et les emails. Les participants n'ont pas de compte à créer : ils ouvrent le lien reçu et découvrent la personne tirée et sa liste d'envies. Ceux qui préfèrent ne pas donner leur adresse professionnelle choisissent leur prénom depuis le lien du groupe. Les adresses ne servent qu'au tirage et aux deux rappels, et chaque email a un lien de désinscription.

## Les exclusions : équipes, managers, binômes

Au bureau, deux exclusions reviennent souvent. Les collègues d'un même bureau, qui se connaissent déjà et préfèrent offrir à d'autres services. Et le manager et son équipe directe, pour éviter qu'un cadeau ressemble à une faveur. Sur Listou, une gommette de couleur par équipe suffit : même gommette, pas de tirage entre eux. On peut aussi exclure une personne précise.

## Des envies, pour ne pas offrir au hasard

Le tirage dit à qui offrir, pas quoi. Entre collègues, on connaît rarement les goûts de la personne tirée, et c'est là que les listes d'envies sauvent le jeu. Demandez à chacun deux ou trois idées dans le budget : un thé préféré, une plante pour le bureau, un livre. Sur Listou, chacun note ses envies dans sa liste, et la personne qui l'a tiré la voit en ouvrant son tirage.

Pour qui n'a pas d'idée, notre [guide des cadeaux par budget](/blog/idees-cadeaux-secret-santa) donne des pistes à 10, 20 et 30 €.

## Les cadeaux à éviter au bureau

- **L'alcool**, sauf si l'on sait que la personne en boit.
- **Le parfum, les cosmétiques et les vêtements**, trop personnels pour quelqu'un qu'on connaît peu.
- **Le cadeau « blague »** qui ne fait rire que celui qui l'offre.
- **Tout ce qui touche à la religion, à la politique ou au physique.**
- **Le cadeau de la boutique de l'entreprise** : un stylo à logo n'est pas un cadeau.

En cas de doute, un cadeau gourmand, un objet pour le bureau ou une carte cadeau d'un lieu que la personne aime restent sûrs.

## Le jour J

Les cadeaux emballés, sans nom de l'expéditeur, sont déposés dans un coin la veille ou le matin même. À l'heure dite, chacun ouvre le sien et devine qui était son Père Noël. Prévoyez une solution pour les absents : un cadeau laissé sur le bureau, ou un envoi postal pour le collègue en télétravail complet. Et pour l'année prochaine, gardez le groupe : Listou reprend les participants et leurs équipes et écarte les tirages de l'an passé.
`,
  faq: [
    [
      "Quel budget pour un Secret Santa entre collègues ?",
      "10 à 15 € maximum. C'est assez pour un cadeau qui fait plaisir et assez peu pour que tout le monde participe, y compris les stagiaires et les temps partiels. Au-delà de 20 €, certains s'abstiendront sans le dire.",
    ],
    [
      "Un Secret Santa au bureau est-il obligatoire ?",
      "Non, et il faut le dire dans l'annonce. Ne tirez au sort que les collègues qui ont répondu oui avant la date limite.",
    ],
    [
      "Quand annoncer le Secret Santa au bureau ?",
      "Trois semaines avant l'échange pour l'annonce, deux semaines avant pour le tirage. Il faut le temps de lire les envies de la personne tirée et de recevoir une commande.",
    ],
    [
      "Comment tirer au sort avec des collègues en télétravail ?",
      "En ligne. Chacun reçoit son tirage par email ou retrouve son prénom sur le lien du groupe, sans compte à créer. Le tirage exclut d'office qu'on tombe sur soi-même.",
    ],
    [
      "Faut-il exclure le manager du tirage avec son équipe ?",
      "C'est prudent : un cadeau entre un manager et un membre de son équipe peut ressembler à une faveur. Sur Listou, une gommette commune au manager et à son équipe les empêche de se tirer entre eux.",
    ],
    [
      "Quels cadeaux éviter entre collègues ?",
      "L'alcool sans savoir si la personne en boit, le parfum et les vêtements, le cadeau blague, et tout ce qui touche à la religion, à la politique ou au physique. Un cadeau gourmand ou un objet pour le bureau reste sûr.",
    ],
    [
      "Les emails professionnels des collègues sont-ils protégés ?",
      "Sur Listou, les adresses ne servent qu'au tirage et à deux rappels, ne sont jamais montrées aux autres participants, et chaque email contient un lien de désinscription. Ceux qui ne veulent pas donner d'adresse choisissent leur prénom depuis le lien du groupe.",
    ],
  ],
  cta: "secret_santa",
};
